import React, { useState, useMemo, useCallback } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import PackersNumpad from "@/components/packers/PackersNumpad";
import PackersToolbar from "@/components/packers/PackersToolbar";
import PackerOrderCard from "@/components/packers/PackerOrderCard";
import { usePackersList, useMarkPrinted, usePrintLabel } from "@/hooks/usePackers";
import type { PackerOrder } from "@/hooks/usePackers";
import { generateEExactXml, downloadEExactXml } from "@/services/export/eExactXmlGenerator";
import type { EExactOrder } from "@/services/export/eExactXmlGenerator";
import { toast } from "sonner";

/** An order is "new / eligible for download" when:
 *  1. Not yet printed
 *  2. Not scheduled for future shipping
 *  3. Every non-ignored line item has a base_paint match */
const isEligibleNew = (order: PackerOrder): boolean => {
  if (order.is_printed || order.is_future_shipping) return false;
  const relevant = order.line_items.filter(li => !li.base_paint?.ignored_brand);
  if (relevant.length === 0) return false;
  return relevant.every(li => li.base_paint != null);
};

const Packers = () => {
  const [numpadFilter, setNumpadFilter] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, refetch } = usePackersList({ limit: 200 });
  const markPrinted = useMarkPrinted();
  const printLabel = usePrintLabel();

  const hasRelevantItems = (order: PackerOrder): boolean =>
    order.line_items.some(li => !li.base_paint?.ignored_brand);

  const orders = (data?.data || []).filter(hasRelevantItems);
  const meta = data?.meta || { total: 0, limit: 0, offset: 0, packed_today: 0 };

  const filteredOrders = useMemo(() => {
    if (!numpadFilter) return orders;
    return orders.filter((o) => o.ordernumber.endsWith(numpadFilter));
  }, [orders, numpadFilter]);

  const toggleSelect = useCallback((ordernumber: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ordernumber)) next.delete(ordernumber);
      else next.add(ordernumber);
      return next;
    });
  }, []);

  const selectAll = () => {
    if (selected.size === filteredOrders.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredOrders.map((o) => o.ordernumber)));
    }
  };

  const printSelected = () => {
    if (selected.size === 0) return;
    const ordernumbers = [...selected];
    printLabel.mutate(ordernumbers, {
      onSuccess: () => {
        markPrinted.mutate(ordernumbers, { onSuccess: () => setSelected(new Set()) });
      },
    });
  };

  const handlePrintLabel = (ordernumber: string) => {
    printLabel.mutate([ordernumber]);
  };

  const printNew = () => {
    const eligible = orders.filter(isEligibleNew).map((o) => o.ordernumber);
    if (eligible.length === 0) return;
    markPrinted.mutate(eligible);
  };

  const handleExportXml = () => {
    const selectedOrders = orders.filter(o => selected.has(o.ordernumber));
    const eExactOrders: EExactOrder[] = selectedOrders.map(order => ({
      ordernumber: order.ordernumber,
      lines: order.line_items
        .filter(li => li.base_paint?.booking_product?.product_id)
        .map(li => ({
          itemCode: li.base_paint!.booking_product!.product_id,
          text: [
            li.producttitle,
            [li.color_code, li.collection_name].filter(Boolean).join(' - ')
          ].filter(Boolean).join(' | '),
          quantity: li.quantity,
        })),
    })).filter(o => o.lines.length > 0);

    if (eExactOrders.length === 0) {
      toast.error('Geen orders met boekingsproducten gevonden');
      return;
    }

    try {
      const xml = generateEExactXml(eExactOrders);
      downloadEExactXml(xml);
      toast.success(`XML geëxporteerd voor ${eExactOrders.length} order(s)`);
    } catch (e) {
      toast.error('Fout bij genereren XML bestand');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1f3d] to-[#0f1221] p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-white text-xl font-bold">Kleuren Orders</h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => refetch()}
          className="text-white/60 hover:text-white hover:bg-white/10"
        >
          <RefreshCw className="w-5 h-5" />
        </Button>
      </div>

      {/* Numpad */}
      <PackersNumpad filter={numpadFilter} onFilterChange={setNumpadFilter} />

      {/* Toolbar */}
      <PackersToolbar
        total={meta.total}
        packedToday={meta.packed_today}
        selectedCount={selected.size}
        newCount={orders.filter(isEligibleNew).length}
        onSelectAll={selectAll}
        onPrintSelected={printSelected}
        onPrintNew={printNew}
        onExportXml={handleExportXml}
        isPrinting={markPrinted.isPending || printLabel.isPending}
      />

      {/* Orders */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-white/40 animate-spin" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-20 text-white/40">
          {numpadFilter
            ? `Geen orders eindigend op ...${numpadFilter}`
            : "Geen orders beschikbaar"}
        </div>
      ) : (
        <div className="space-y-3 pb-20">
          {filteredOrders.map((order) => (
            <PackerOrderCard
              key={order.ordernumber}
              order={order}
              selected={selected.has(order.ordernumber)}
              onToggleSelect={toggleSelect}
              onPrintLabel={handlePrintLabel}
              isPrintingLabel={printLabel.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Packers;
